import { useEffect, useState } from "react";
import axios from "axios";

import { API } from "../../lib/api";

/**
 * Phase 5c — Owner Acknowledgment block (public, token-gated).
 *
 * Two states:
 *   1. Not yet acknowledged from this token → CTA + optional inputs
 *   2. Acknowledged → green confirmation card with timestamp + name + comment
 *
 * Persistence:
 *   - localStorage["zion.ack." + token] holds {id, created_at, name, comment}
 *     so a hard refresh feels instant.
 *   - On mount we ALSO call GET /acknowledgment-status to handle a fresh
 *     device whose localStorage is empty.
 */
export default function OwnerAcknowledgmentBlock({ token }) {
  const lsKey = `zion.ack.${token}`;
  const [acked, setAcked] = useState(() => {
    try {
      const raw = localStorage.getItem(lsKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Reconcile against the server in case localStorage was cleared.
  useEffect(() => {
    let cancelled = false;
    if (acked) return; // trust local state when present
    (async () => {
      try {
        const r = await axios.get(
          `${API}/public/reports/${token}/acknowledgment-status`
        );
        if (cancelled) return;
        if (r.data?.acknowledged) {
          const remote = {
            id: null,
            created_at: r.data.acknowledged_at,
            acknowledger_name: r.data.acknowledger_name,
            comment: null,
          };
          setAcked(remote);
        }
      } catch {
        /* silent — pre-ack state is the safe default */
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const body = {};
      if (name.trim()) body.acknowledger_name = name.trim();
      if (email.trim()) body.acknowledger_email = email.trim();
      if (comment.trim()) body.comment = comment.trim();
      const r = await axios.post(
        `${API}/public/reports/${token}/acknowledge`,
        body
      );
      const local = {
        id: r.data?.id || null,
        created_at: r.data?.created_at || new Date().toISOString(),
        acknowledger_name: r.data?.acknowledger_name || name.trim() || null,
        comment: comment.trim() || null,
      };
      try {
        localStorage.setItem(lsKey, JSON.stringify(local));
      } catch {
        /* private mode — fine, server is source of truth */
      }
      setAcked(local);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        setErr(
          (detail && detail.message) ||
            "Please wait a minute before acknowledging again."
        );
      } else if (status === 410 && detail && typeof detail === "object") {
        setErr(detail.message || "This link is no longer active.");
      } else {
        setErr("Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (acked) {
    return (
      <section
        data-testid="public-ack-confirmed"
        className="mt-12 rounded-md border-l-4 border-emerald-500 bg-emerald-50/70 px-5 py-5"
      >
        <div className="flex items-start gap-3">
          <div
            aria-hidden
            className="mt-0.5 inline-flex h-6 w-6 flex-none items-center justify-center rounded-full bg-emerald-600 text-white text-sm font-bold"
          >
            ✓
          </div>
          <div className="min-w-0">
            <div className="font-heading text-base font-semibold text-emerald-900">
              Report acknowledged
            </div>
            <div className="text-sm text-emerald-900/80 mt-0.5">
              {acked.created_at
                ? new Date(acked.created_at).toLocaleString()
                : "Just now"}
              {acked.acknowledger_name ? (
                <>
                  {" · "}
                  <span data-testid="public-ack-name" className="font-medium">
                    {acked.acknowledger_name}
                  </span>
                </>
              ) : null}
            </div>
            {acked.comment && (
              <div
                data-testid="public-ack-comment"
                className="mt-2 text-sm text-emerald-900/90 whitespace-pre-wrap"
              >
                {acked.comment}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      data-testid="public-ack-block"
      className="mt-12 rounded-md border-l-4 border-amber-500 bg-amber-50/40 px-5 py-5"
    >
      <h2 className="font-heading text-base font-semibold text-slate-900">
        Acknowledge this report
      </h2>
      <p className="text-sm text-slate-700 mt-1">
        Tap below to confirm you&rsquo;ve reviewed this report.
      </p>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            Your name (optional)
          </span>
          <input
            data-testid="public-ack-name-input"
            type="text"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="Mark Stevens"
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
            Email (optional)
          </span>
          <input
            data-testid="public-ack-email-input"
            type="email"
            value={email}
            maxLength={254}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          />
        </label>
      </div>

      <label className="block mt-3">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">
          Comment (optional)
        </span>
        <textarea
          data-testid="public-ack-comment-input"
          value={comment}
          rows={3}
          maxLength={1000}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Anything we should know?"
          className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
        />
      </label>

      {err && (
        <div
          data-testid="public-ack-error"
          className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800"
        >
          {err}
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          data-testid="public-ack-submit"
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center justify-center rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {busy ? "Submitting…" : "I acknowledge this report"}
        </button>
        <span
          data-testid="public-ack-disclaimer"
          className="text-xs text-slate-500"
        >
          This is not a legal e-signature.
        </span>
      </div>
    </section>
  );
}
